/**
 * OperationInspectorReactiveViz — inspector for reactive_visualization nodes.
 *
 * ADR-0056 §8.2. ADR-0129 — on the shared spine: Info (node label) · Setup
 * (Config + SQL as sub-tabs, incl. Parameters + Row selection) · Data (chart
 * preview + raw spec) · AI (viz copilot, frame-filling chat). A persistent Run
 * button above the tab rack executes the node end-to-end: saves the spec, runs
 * the graph up to this node via execute-preview, re-renders the chart, and
 * populates the cached-input row so the AI agent can see the real upstream
 * schema.
 *
 * Width discipline: every growable element (CodeMirror, results grid,
 * JSON textareas, chat panel) uses `minWidth: 0` + `maxWidth: "100%"` +
 * `overflowX: "auto"` so long SQL never expands the inspector panel.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aiRouteErrorCode } from "../../lib/aiRouteError.js";
import type { Node } from "@xyflow/react";
import CodeMirror from "@uiw/react-codemirror";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import { useAiChatThread } from "../../hooks/useAiChatThread.js";
import { useOperationRun } from "../../hooks/useOperationRun.js";
import {
  ChatMessageList,
  ChatComposer,
  ChatThinkingIndicator,
  ChatToolPulse,
  ChatErrorBanner
} from "../aiChat/index.js";
import { useToast } from "../Toast.js";
import { useConfirm } from "../ConfirmDialog.js";
import type {
  ReactiveParam,
  ReactiveParamType,
  ReactiveParamNullBehavior,
  ReactiveVisualizationSpec,
} from "../../lib/reactiveVisualization.js";
import { Chart } from "../Chart.js";
import { echartsSpecToOption } from "../../lib/echartsRenderer/index.js";
import type { EchartsVizSpecV1 } from "../../lib/echartsSpec.js";
import { validateEchartsSpec } from "../../lib/echartsSpec.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";

const sqlExtensions = [sql({ dialect: PostgreSQL })];

// Module-level so run state survives inspector remounts. The inspector is
// unmounted whenever the user clicks away and remounted on re-select —
// without this, `runBusy` resets to false even though the server is still
// processing the earlier Run, letting the user fire a duplicate request.
const runStartTimeByNodeId = new Map<string, number>();
const lastRunMetaByNodeId = new Map<string, {
  lastRunAt: string | null;
  lastRowCount: number | null;
  resultsDetail: { columns: string[]; rows: unknown[][] } | null;
}>();

// Hard cap so extremely wide inspectors don't sprawl across the page.
// The value matches the inspector card's own fixed-ish width per the
// existing viz / transform inspectors.
const INSPECTOR_WIDTH_STYLE: React.CSSProperties = { width: "100%", maxWidth: "100%", minWidth: 0 };

// ──────────────────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────────────────

interface Props {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  flushAndSave: () => Promise<boolean>;
  /** Tenant-selectable AI models. Shape matches TenantAiModelRow in OperationEditor. */
  tenantAiModels?: Array<{ model_id: string; status: string }>;
  /** Currently-selected AI model id (mirrors OperationEditor's state). */
  selectedAiModelId?: string;
  /** Caller updates state and debounce-saves the account preference. */
  onTenantAiModelChange?: (modelId: string) => void;
  /**
   * Writes run status ("running" / "success" / "error" / "idle") onto the
   * target node's canvas data. Drives the spinner ring the viz / provider
   * inspectors use. Optional because tests mount the inspector standalone.
   */
  setRunStatus?: (nodeIds: string[], status: "running" | "success" | "error" | "idle") => void;
  /**
   * ADR-0076 §3.1: whether this viz node has an inbound `data` edge in the
   * canvas graph. When false, the AI chat Send button is disabled to avoid a
   * server round-trip we know would hit the no-data soft-reply path.
   */
  hasIncomingDataEdge: boolean;
}

type InteractionMode = "ask" | "agent";

type SetupSubTab = "config" | "sql";

// ──────────────────────────────────────────────────────────────────────
// Spec extraction / update helpers
// ──────────────────────────────────────────────────────────────────────

function extractSpec(nodeConfig: Record<string, unknown>): ReactiveVisualizationSpec {
  const raw = nodeConfig.spec_reactive_visualization;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const r = raw as Record<string, unknown>;
    return {
      specVersion: "reactive_visualization.v1",
      sql: typeof r.sql === "string" ? r.sql : "",
      parameters: Array.isArray(r.parameters) ? (r.parameters as ReactiveParam[]) : [],
      viz: r.viz && typeof r.viz === "object" ? (r.viz as ReactiveVisualizationSpec["viz"]) : { option: {} },
      ...(r.rowSelection === "single" || r.rowSelection === "multi" || r.rowSelection === "none"
        ? { rowSelection: r.rowSelection }
        : {}),
    };
  }
  return {
    specVersion: "reactive_visualization.v1",
    sql: "",
    parameters: [],
    viz: { option: {} },
  };
}

function setSpec(
  setNodeData: Props["setNodeData"],
  nodeId: string,
  updater: (prev: ReactiveVisualizationSpec) => ReactiveVisualizationSpec
): void {
  setNodeData(nodeId, (data) => {
    const prev = extractSpec(data.config);
    const next = updater(prev);
    return {
      ...data,
      config: { ...data.config, spec_reactive_visualization: next },
    };
  });
}

// ──────────────────────────────────────────────────────────────────────
// AI chat types
// ──────────────────────────────────────────────────────────────────────

type AiChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
  artifactSpec?: ReactiveVisualizationSpec;
  investigationIncomplete?: boolean;
};

type AiChatResponse = {
  assistantMessage: { id: string; displayText: string };
  mergedSpec: ReactiveVisualizationSpec | null;
  coordinatorResponse?: { investigation_incomplete?: boolean };
  meta: { durationMs: number; responseMode: "edit" | "clarify" | "chat" };
};

const TOOL_PULSE_LABELS: Record<string, string> = {
  get_upstream_schema: "checking upstream schema…",
  run_sql_preview: "running SQL preview…",
  get_dataset_source: "inspecting dataset…",
  get_distinct_values: "checking column values…",
  render_chart_image: "rendering chart…",
};

// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────

export function OperationInspectorReactiveViz({
  selectedNode,
  operationId,
  setNodeData,
  flushAndSave,
  tenantAiModels,
  selectedAiModelId,
  onTenantAiModelChange,
  setRunStatus,
  hasIncomingDataEdge,
}: Props) {
  const nodeId = selectedNode.id;
  const spec = useMemo(() => extractSpec(selectedNode.data.config), [selectedNode.data.config]);
  const toast = useToast();
  const confirm = useConfirm();
  const runController = useOperationRun();
  const [interactionMode, setInteractionMode] = useState<InteractionMode>("agent");

  const [setupSubTab, setSetupSubTab] = useState<SetupSubTab>("sql");
  const [runError, setRunError] = useState<string | null>(null);
  // Initialize from the module-level store so state survives remounts.
  const [lastRunAt, setLastRunAt] = useState<string | null>(
    () => lastRunMetaByNodeId.get(nodeId)?.lastRunAt ?? null
  );
  const [lastRowCount, setLastRowCount] = useState<number | null>(
    () => lastRunMetaByNodeId.get(nodeId)?.lastRowCount ?? null
  );
  const [resultsDetail, setResultsDetail] = useState<{ columns: string[]; rows: unknown[][] } | null>(
    () => lastRunMetaByNodeId.get(nodeId)?.resultsDetail ?? null
  );
  // Drive runBusy from the node's canvas runStatus so it survives remounts.
  // The actual fetch's .then/.finally still writes results via setNodeData
  // (a stable prop from OperationEditor) so completion is never lost.
  const runBusy = selectedNode.data.runStatus === "running";
  const [runElapsedMs, setRunElapsedMs] = useState<number>(() => {
    const start = runStartTimeByNodeId.get(nodeId);
    return start ? Date.now() - start : 0;
  });

  // Tick the elapsed-time counter while a run is in progress.
  useEffect(() => {
    if (!runBusy) return;
    const handle = window.setInterval(() => {
      const start = runStartTimeByNodeId.get(nodeId);
      if (start) setRunElapsedMs(Date.now() - start);
    }, 250);
    return () => window.clearInterval(handle);
  }, [runBusy, nodeId]);

  // ADR-0080: on mount (or node switch), pull the cached `render_out` so the
  // SQL Results grid + the typed-spec chart preview can render immediately
  // without forcing a fresh Run. The render model carries `detailData`
  // (SQL output rows + columns) — that's what `resultsDetail` and the typed
  // renderer's `dimensions` need. Mirrors `OperationInspectorTanTable`'s
  // on-mount cache hydration. Only refetches if the in-memory cache is
  // empty (fresh page load / after a different-node detour that evicted
  // module-level state).
  useEffect(() => {
    if (resultsDetail !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch<{
          renderModel?: { detailData?: { columns?: string[]; rows?: unknown[][] }; lastRunAt?: string | null };
          lastRunAt?: string | null;
          lastRowCount?: number | null;
        }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/cache?port_id=render_out`
        );
        if (cancelled) return;
        const detail = data.renderModel?.detailData;
        if (detail && Array.isArray(detail.columns) && Array.isArray(detail.rows)) {
          const next = { columns: detail.columns, rows: detail.rows };
          setResultsDetail(next);
          if (typeof data.lastRunAt === "string") setLastRunAt(data.lastRunAt);
          if (typeof data.lastRowCount === "number") setLastRowCount(data.lastRowCount);
          // Backfill the module-level survival cache so future remounts
          // within the same browser session don't refetch.
          lastRunMetaByNodeId.set(nodeId, {
            lastRunAt: typeof data.lastRunAt === "string" ? data.lastRunAt : null,
            lastRowCount: typeof data.lastRowCount === "number" ? data.lastRowCount : null,
            resultsDetail: next,
          });
        }
      } catch (e) {
        // 404 = no cache yet (node never run). Anything else is non-fatal —
        // the inspector's chart preview will surface column-drift inline.
        if (e instanceof ApiRequestError && e.status === 404) return;
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationId, nodeId]);

  // SQL draft
  const [sqlDraft, setSqlDraft] = useState<string>(spec.sql);
  useEffect(() => { setSqlDraft(spec.sql); }, [nodeId, spec.sql]);

  const commitSql = useCallback(() => {
    if (sqlDraft === spec.sql) return;
    setSpec(setNodeData, nodeId, (prev) => ({ ...prev, sql: sqlDraft }));
  }, [sqlDraft, spec.sql, setNodeData, nodeId]);

  // ────────── parameters section ──────────
  const addParameter = useCallback(() => {
    setSpec(setNodeData, nodeId, (prev) => ({
      ...prev,
      parameters: [...prev.parameters, { name: `param_${prev.parameters.length + 1}`, type: "text", null_behavior: "all_rows" }],
    }));
  }, [setNodeData, nodeId]);

  const updateParameter = useCallback((index: number, patch: Partial<ReactiveParam>) => {
    setSpec(setNodeData, nodeId, (prev) => {
      const next = prev.parameters.slice();
      const existing = next[index];
      if (!existing) return prev;
      next[index] = { ...existing, ...patch };
      return { ...prev, parameters: next };
    });
  }, [setNodeData, nodeId]);

  const removeParameter = useCallback((index: number) => {
    setSpec(setNodeData, nodeId, (prev) => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index),
    }));
  }, [setNodeData, nodeId]);

  // ────────── Run button — executes the node + refreshes preview ──────────
  // NOTE: no auto-run on mount. Runs can be slow on large upstream datasets
  // (DuckDB row-load is linear in upstream rows), so we only execute when
  // the user clicks Run. The AI chat also has an explicit "Populate input
  // cache first" message if they try to chat before running at least once.
  const runNode = useCallback(async (opts?: { silent?: boolean }) => {
    // Dedupe via the module-level start map — survives remounts.
    if (runStartTimeByNodeId.has(nodeId)) return;
    setRunError(null);
    runStartTimeByNodeId.set(nodeId, Date.now());
    setRunElapsedMs(0);
    setRunStatus?.([nodeId], "running");
    try {
      const saved = await flushAndSave();
      if (!saved) {
        setRunStatus?.([nodeId], "idle");
        runStartTimeByNodeId.delete(nodeId);
        return;
      }
      // ADR-0128 — async run: POST → poll runs/:runId. The render model
      // (with `detailData`) is read from the node cache (`render_out`) after
      // the run, not from the receipt.
      await runController.startRun({
        operationId,
        nodeId,
        forceRefresh: false,
        onComplete: async (_run, nodes) => {
          const target = nodes.find((n) => n.node_id === nodeId) ?? null;
          const lastRunAtVal = target?.finished_at ?? new Date().toISOString();
          const lastRowCountVal = target?.row_count ?? null;
          setLastRunAt(lastRunAtVal);
          setLastRowCount(lastRowCountVal);
          let resultsDetailVal: { columns: string[]; rows: unknown[][] } | null = null;
          try {
            const cache = await apiFetch<{
              renderModel?: { detailData?: { columns?: string[]; rows?: unknown[][] } };
            }>(
              `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/cache?port_id=render_out`
            );
            const detail = cache.renderModel?.detailData;
            if (detail && Array.isArray(detail.columns) && Array.isArray(detail.rows)) {
              resultsDetailVal = { columns: detail.columns, rows: detail.rows };
              setResultsDetail(resultsDetailVal);
            }
          } catch { /* preview best-effort — the run itself succeeded */ }
          // Persist meta for a future remount of this inspector.
          lastRunMetaByNodeId.set(nodeId, {
            lastRunAt: lastRunAtVal,
            lastRowCount: lastRowCountVal,
            resultsDetail: resultsDetailVal
          });
          setRunStatus?.([nodeId], "success");
          if (!opts?.silent) {
            toast.push({ kind: "success", message: `Executed — ${(lastRowCountVal ?? 0).toLocaleString()} rows` });
          }
        },
        onError: (error) => {
          setRunError(error ?? "Run failed.");
          setRunStatus?.([nodeId], "error");
          if (!opts?.silent && error) toast.push({ kind: "error", message: error });
        },
        onCancelled: () => { setRunStatus?.([nodeId], "idle"); },
        onFailure: (err) => {
          const msg = err instanceof Error ? err.message : String(err);
          setRunError(msg);
          setRunStatus?.([nodeId], "error");
          if (!opts?.silent) toast.push({ kind: "error", message: msg });
        },
      });
    } finally {
      runStartTimeByNodeId.delete(nodeId);
    }
  }, [flushAndSave, operationId, nodeId, toast, setRunStatus, runController]);

  // ────────── AI chat ──────────
  // Session lifecycle (load-latest + poll-while-trailing-user) lives in
  // `useAiChatThread` per spec §5.1 / ADR-0076 expansion. This was the
  // canonical reference implementation prior to the rollout; the hook now
  // owns the lifecycle for every chat-bearing surface. Local state below
  // covers active-mount send + composer draft + tool-call pulses only.
  const {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    replyPending,
    error: chatThreadError,
    latestToolPulse: serverToolPulse,
    resetChat,
    cancelChat,
  } = useAiChatThread<AiChatMessage>({
    threadKey: `${operationId}/${nodeId}/reactive-viz`,
    loadLatest: useCallback(async () => {
      const res = await apiFetch<{
        session: { id: string; created_at: string; latest_tool_pulse?: string | null };
        messages: Array<{ id: string; role: string; content: string; created_at: string }>;
      }>(`/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/reactive-visualization/sessions/latest`);
      return {
        sessionId: res.session.id,
        messages: res.messages.map((m) => ({
          id: m.id,
          role: m.role as AiChatMessage["role"],
          content: m.content,
          created_at: m.created_at,
        })),
        latestToolPulse: res.session.latest_tool_pulse ?? null,
      };
    }, [operationId, nodeId]),
    createNewSession: useCallback(async () => {
      const res = await apiFetch<{ session: { id: string } }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/reactive-visualization/sessions`,
        { method: "POST", body: {} }
      );
      return { sessionId: res.session.id };
    }, [operationId, nodeId]),
    cancelChat: useCallback(async (sid: string) => {
      await apiFetch(
        `/api/tenant/ai/sessions/${encodeURIComponent(sid)}/abort`,
        { method: "POST", body: {} }
      );
    }, []),
  });
  const [draftMessage, setDraftMessage] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  // ADR-0207 §3.4: the hub's error code, for the admin's link under an AI-route refusal.
  const [chatErrorCode, setChatErrorCode] = useState<string | null>(null);
  const [toolPulse, setToolPulse] = useState<string | null>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatPanelRef.current) {
      chatPanelRef.current.scrollTop = chatPanelRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const text = draftMessage.trim();
    if (!text) return;
    setChatError(null); setChatErrorCode(null);
    setChatBusy(true);
    try {
      // ADR-0076 §3.2: flush autosave before opening the SSE stream so the
      // server's session/node lookup sees the just-edited graph state. Mirrors
      // OperationInspectorTanTable / OperationInspectorKpiCard.
      const saved = await flushAndSave();
      if (!saved) {
        setChatBusy(false);
        return;
      }
      let sid = sessionId;
      if (!sid) {
        const res = await apiFetch<{ session: { id: string; created_at: string } }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/reactive-visualization/sessions`,
          { method: "POST", body: {} }
        );
        sid = res.session.id;
        setSessionId(sid);
      }
      const userOptimistic: AiChatMessage = {
        id: `local-${Date.now()}`,
        role: "user",
        content: text,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userOptimistic]);
      setDraftMessage("");

      // SSE stream: pulses during tool calls, then a terminal `complete` or
      // `error` event with the full response. Using fetch directly (not
      // apiFetch) so we can read the ReadableStream.
      const streamRes = await fetch(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/reactive-visualization/sessions/${encodeURIComponent(sid)}/messages`,
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            message: text,
            ...(selectedAiModelId ? { model: selectedAiModelId } : {}),
            interactionMode,
          }),
        }
      );
      if (!streamRes.ok) {
        const errBody = await streamRes.text();
        throw new Error(`reactive viz chat failed: ${streamRes.status} ${errBody}`);
      }
      if (!streamRes.body) throw new Error("reactive viz chat: no response body");

      let completePayload: AiChatResponse | null = null;
      let errorPayload: { code: string; message: string } | null = null;
      const reader = streamRes.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamDone = false;
      while (!streamDone) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE events are separated by blank lines. Process each event in the buffer.
        let eventBreak: number;
        while ((eventBreak = buffer.indexOf("\n\n")) >= 0) {
          const rawEvent = buffer.slice(0, eventBreak);
          buffer = buffer.slice(eventBreak + 2);
          const dataLine = rawEvent.split("\n").find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          let payload: Record<string, unknown>;
          try {
            payload = JSON.parse(dataLine.slice(5).trim());
          } catch {
            continue;
          }
          const kind = typeof payload.kind === "string" ? payload.kind : "";
          if (kind === "tool_call_start" && typeof payload.toolName === "string") {
            setToolPulse(TOOL_PULSE_LABELS[payload.toolName] ?? `running ${payload.toolName}…`);
          } else if (kind === "iteration_start") {
            // Y bundle / migration 131: between tool calls (and at the
            // start, before any tool fires), show "thinking…" so the
            // active mount has the same phase information that the
            // remount path now persists via `latest_tool_pulse`.
            setToolPulse("thinking…");
          } else if (kind === "iteration_cap_hit") {
            setToolPulse("finalizing (hit tool-call cap)…");
          } else if (kind === "complete") {
            completePayload = payload as unknown as AiChatResponse;
            streamDone = true;
          } else if (kind === "error") {
            errorPayload = {
              code: String(payload.code ?? "error"),
              message: String(payload.message ?? "reactive viz chat failed"),
            };
            streamDone = true;
          }
        }
      }
      setToolPulse(null);
      if (errorPayload) throw new Error(errorPayload.message);
      if (!completePayload) throw new Error("reactive viz chat: stream ended without a complete event");

      const investigationIncomplete =
        completePayload.coordinatorResponse?.investigation_incomplete === true;
      const assistantMsg: AiChatMessage = {
        id: completePayload.assistantMessage.id,
        role: "assistant",
        content: completePayload.assistantMessage.displayText,
        created_at: new Date().toISOString(),
        ...(completePayload.mergedSpec ? { artifactSpec: completePayload.mergedSpec } : {}),
        ...(investigationIncomplete ? { investigationIncomplete: true } : {}),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      if (completePayload.mergedSpec) {
        setSpec(setNodeData, nodeId, () => completePayload!.mergedSpec!);
        void flushAndSave().then(() => runNode({ silent: true }));
      }
    } catch (e) {
      setChatError(e instanceof Error ? e.message : String(e)); setChatErrorCode(aiRouteErrorCode(e));
    } finally {
      setToolPulse(null);
      setChatBusy(false);
    }
  }, [draftMessage, sessionId, operationId, nodeId, selectedAiModelId, interactionMode, setNodeData, flushAndSave, runNode]);

  // ────────── chart preview source ──────────
  // ADR-0079 / ADR-0080: spec.viz is `{ spec: EchartsVizSpecV1 }`. Migration
  // 129 force-cutover any pre-existing `{ option }` legacy blobs to typed-spec
  // placeholders, so this read path no longer needs a legacy branch.
  const typedSpec: EchartsVizSpecV1 | null =
    "spec" in spec.viz && spec.viz.spec ? (spec.viz.spec as EchartsVizSpecV1) : null;

  const typedRenderResult = useMemo(() => {
    if (!typedSpec) return null;
    const rows = resultsDetail?.rows ?? [];
    const dimensions = resultsDetail?.columns ?? [];
    return echartsSpecToOption(typedSpec, rows, dimensions);
  }, [typedSpec, resultsDetail]);

  // ────────── render ──────────
  const infoContent = (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div className="field">
        <label className="label">Node Name</label>
        <input
          className="input"
          type="text"
          value={selectedNode.data.label}
          onChange={(e) => setNodeData(nodeId, (d) => ({ ...d, label: e.target.value }))}
          onBlur={() => void flushAndSave()}
          placeholder="Unnamed"
        />
      </div>
    </div>
  );

  return (
    <div style={{ ...INSPECTOR_WIDTH_STYLE, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Persistent action bar — node-global, above the tab rack. */}
      <div
        style={{
          ...INSPECTOR_WIDTH_STYLE,
          display: "flex",
          gap: 4,
          alignItems: "center",
          borderBottom: "1px solid var(--line)",
          paddingBottom: 6,
          marginBottom: 8,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <div className="muted" style={{ fontSize: 12, marginRight: 8 }}>
          {runBusy ? (
            <>Running… {Math.floor(runElapsedMs / 1000)}s</>
          ) : lastRunAt ? (
            <>
              Last run: {new Date(lastRunAt).toLocaleString()}
              {typeof lastRowCount === "number" ? ` · ${lastRowCount.toLocaleString()} rows` : ""}
            </>
          ) : (
            <>Not yet executed</>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btnPrimary btnSm" type="button" onClick={() => void runNode()} disabled={runBusy}>
          {runBusy ? `Running… ${Math.floor(runElapsedMs / 1000)}s` : "▶ Run"}
        </button>
      </div>

      {runError ? (
        <div className="help" style={{ ...INSPECTOR_WIDTH_STYLE, flexShrink: 0, marginBottom: 8 }}>⚠ {runError}</div>
      ) : null}

      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="reactive_visualization"
        defaultSlot="setup"
        tabs={[
          { slot: "info", content: infoContent },
          {
            slot: "setup",
            content: (
              <div style={{ display: "grid", gap: 10 }}>
                <div className="reportTabs" role="tablist" aria-label="Reactive viz setup sections">
                  <button className={`reportTabBtn${setupSubTab === "config" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setSetupSubTab("config")}>Config</button>
                  <button className={`reportTabBtn${setupSubTab === "sql" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setSetupSubTab("sql")}>SQL</button>
                </div>
                {setupSubTab === "config" ? (
        <div className="card" style={INSPECTOR_WIDTH_STYLE}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 600 }}>Parameters</div>
            <button className="btn btnSm" type="button" onClick={addParameter}>+ Add parameter</button>
          </div>
          {spec.parameters.length === 0 ? (
            <div className="muted">No parameters declared yet.</div>
          ) : (
            <div className="tableWrap" style={{ ...INSPECTOR_WIDTH_STYLE, overflowX: "auto" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Null behavior</th>
                    <th>Allowed values (identifier only)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {spec.parameters.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          className="input"
                          value={p.name}
                          onChange={(e) => updateParameter(i, { name: e.target.value.trim() })}
                        />
                      </td>
                      <td>
                        <select
                          className="select"
                          value={p.type}
                          onChange={(e) => updateParameter(i, { type: e.target.value as ReactiveParamType })}
                        >
                          <option value="text">text</option>
                          <option value="text_array">text_array</option>
                          <option value="number">number</option>
                          <option value="identifier">identifier</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="select"
                          value={p.null_behavior}
                          onChange={(e) => updateParameter(i, { null_behavior: e.target.value as ReactiveParamNullBehavior })}
                        >
                          <option value="all_rows">all_rows</option>
                          <option value="empty">empty</option>
                        </select>
                      </td>
                      <td>
                        {p.type === "identifier" ? (
                          <input
                            className="input"
                            placeholder="week_start, month_start"
                            value={(p.allowed_values ?? []).join(", ")}
                            onChange={(e) => {
                              const vals = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                              updateParameter(i, { allowed_values: vals });
                            }}
                          />
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <button className="adminListAction isDanger" type="button" onClick={() => removeParameter(i)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <label className="field">
              <span className="label">Row selection</span>
              <select
                className="select"
                value={spec.rowSelection ?? "none"}
                onChange={(e) => {
                  const val = e.target.value as "none" | "single" | "multi";
                  setSpec(setNodeData, nodeId, (prev) => ({ ...prev, rowSelection: val }));
                }}
              >
                <option value="none">None</option>
                <option value="single">Single</option>
                <option value="multi">Multi</option>
              </select>
              <div className="help">
                Chart selections fed back into page filter state. Required for drill-down chains.
              </div>
            </label>
          </div>
        </div>
                ) : null}
                {setupSubTab === "sql" ? (
        <div className="card" style={INSPECTOR_WIDTH_STYLE}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>SQL</div>
          <div
            style={{
              ...INSPECTOR_WIDTH_STYLE,
              border: "1px solid var(--line)",
              borderRadius: 6,
              overflow: "auto",
            }}
          >
            <CodeMirror
              value={sqlDraft}
              onChange={setSqlDraft}
              onBlur={commitSql}
              extensions={sqlExtensions}
              height="240px"
              maxWidth="100%"
              basicSetup={{ lineNumbers: true, foldGutter: true }}
            />
          </div>
          <div className="help" style={{ marginTop: 4 }}>
            Use <code>$name</code> for value binding and <code>{`\${name}`}</code> for identifier substitution.
            Always <code>FROM _source</code>.
          </div>

          {/* Results grid */}
          <div style={{ ...INSPECTOR_WIDTH_STYLE, marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Results</div>
            {resultsDetail && resultsDetail.rows.length > 0 ? (
              <div
                style={{
                  ...INSPECTOR_WIDTH_STYLE,
                  overflow: "auto",
                  maxHeight: 260,
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                }}
              >
                <table className="table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      {resultsDetail.columns.map((c, i) => <th key={i}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {resultsDetail.rows.slice(0, 200).map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j} className="mono" style={{ whiteSpace: "nowrap" }}>
                            {cell === null || cell === undefined ? <span className="muted">—</span> : String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {resultsDetail.rows.length > 200 ? (
                  <div className="muted" style={{ padding: 8 }}>Showing first 200 of {resultsDetail.rows.length} rows.</div>
                ) : null}
              </div>
            ) : (
              <div className="muted">
                {runBusy ? "Running…" : "Click Run to execute the SQL against upstream data."}
              </div>
            )}
          </div>
        </div>
                ) : null}
              </div>
            ),
          },
          {
            slot: "data",
            content: (
              <div className="card" style={INSPECTOR_WIDTH_STYLE}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Chart preview</div>
          {typedSpec && typedRenderResult?.ok ? (
            <div style={{ ...INSPECTOR_WIDTH_STYLE, height: 320, minHeight: 320 }}>
              <Chart option={typedRenderResult.option} height={320} />
            </div>
          ) : typedSpec && typedRenderResult && !typedRenderResult.ok ? (
            <div
              className="help"
              style={{ ...INSPECTOR_WIDTH_STYLE, color: "var(--danger-text)", display: "grid", gap: 4 }}
            >
              <div>⚠ Chart spec references columns not present in the upstream data.</div>
              {typedRenderResult.errors.map((err, i) => (
                <div key={i} style={{ marginLeft: 12 }}>{err}</div>
              ))}
              <div className="muted" style={{ marginTop: 4 }}>
                Run the upstream node, or re-author the chart in the AI panel.
              </div>
            </div>
          ) : (
            <div
              className="muted"
              style={{ padding: 12, border: "1px dashed var(--line)", borderRadius: 6, textAlign: "center" }}
            >
              No chart yet — describe what you want in the AI panel, or edit SQL + viz manually.
            </div>
          )}
          {typedSpec ? (
            <RawSpecDisclosure
              spec={typedSpec}
              onCommit={(next) => {
                setSpec(setNodeData, nodeId, (prev) => ({
                  ...prev,
                  viz: { ...prev.viz, spec: next },
                }));
              }}
            />
          ) : null}
        </div>
            ),
          },
          {
            slot: "ai",
            fill: true,
            content: (
              // Mirrors OperationInspectorVisualization's "Ask Atlas" panel shape:
              // pillSelect dropdowns above a scrolled transcript, composer below.
              // `askChatPanelFill` flexes the message list + pins the composer.
              <div className="card askChatPanel askChatPanelFill">
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <select
              className={`pillSelect ${interactionMode === "ask" ? "pillSelectNeutral" : "pillSelectOk"}`}
              value={interactionMode}
              onChange={(e) => setInteractionMode(e.target.value === "ask" ? "ask" : "agent")}
              disabled={chatBusy}
              title={
                interactionMode === "agent"
                  ? "Agent: the AI edits the SQL + chart directly and proposes a spec you can apply."
                  : "Ask: the AI answers questions about the node without proposing spec changes."
              }
            >
              <option value="agent">Agent</option>
              <option value="ask">Ask</option>
            </select>
            {tenantAiModels && tenantAiModels.length > 0 ? (
              <select
                className="pillSelect pillSelectNeutral"
                value={selectedAiModelId ?? ""}
                onChange={(e) => onTenantAiModelChange?.(e.target.value)}
                disabled={chatBusy}
                title="Model for this workspace"
              >
                {tenantAiModels.map((m) => (
                  <option key={m.model_id} value={m.model_id}>
                    {m.model_id}
                    {m.status === "deprecated" ? " (deprecated)" : ""}
                  </option>
                ))}
              </select>
            ) : null}
            <div style={{ flex: 1 }} />
            {/*
              Reset chat — abandons the current session and starts fresh.
              Per spec §10.4 + user request: gives users an explicit escape
              hatch when threads grow too long, when context drifts, or
              when a session deadlocks (e.g. hub-restart-stranded thread
              per spec §3.6.3).
            */}
            <button
              type="button"
              className="btn btnSm"
              onClick={() =>
                void (async () => {
                  const ok = await confirm.confirm({
                    title: "Reset this chat?",
                    detail:
                      "The current conversation is cleared from view and a fresh session starts. Past messages stay on the server but are no longer visible here.",
                    confirmLabel: "Reset chat",
                  });
                  if (!ok) return;
                  await resetChat();
                })()
              }
              disabled={messages.length === 0}
              title="Start a new conversation. Past messages stay on the server but are no longer visible here. Available even mid-thinking — that's the deadlock escape hatch."
            >
              Reset chat
            </button>
          </div>
          <div className="askChatMessages" ref={chatPanelRef}>
            <ChatMessageList<AiChatMessage>
              messages={messages}
              assistantLabel="Atlas Reactive Viz agent"
              emptyState={
                <div className="muted">
                  Describe the chart you want. Example: <em>"show setup hours by week filtered by workcenter"</em>.
                </div>
              }
              renderAssistantExtras={(m) => (
                <>
                  {m.investigationIncomplete ? (
                    <div
                      className="pill pillWarn"
                      style={{ marginTop: 4, display: "inline-block" }}
                      title="The agent hit its tool-call cap before finishing its investigation. The response may be partial — verify before applying."
                    >
                      ⚠ investigation incomplete
                    </div>
                  ) : null}
                  {m.artifactSpec ? (
                    <div className="help" style={{ marginTop: 4 }}>
                      ✓ Applied to node · chart + SQL refreshed
                    </div>
                  ) : null}
                </>
              )}
              trailingNode={
                <>
                  <ChatToolPulse localPulse={toolPulse} serverPulse={serverToolPulse} />
                  <ChatThinkingIndicator visible={replyPending && !toolPulse && !serverToolPulse} />
                </>
              }
            />
          </div>
          <ChatErrorBanner message={chatError ?? chatThreadError} code={chatError ? chatErrorCode : null} />
          <ChatComposer
            draft={draftMessage}
            setDraft={setDraftMessage}
            onSend={() => void sendMessage()}
            onCancel={() => void cancelChat()}
            inFlight={chatBusy || replyPending}
            placeholder={interactionMode === "ask" ? "Ask a question…" : "Describe the chart you want…"}
            disabledExtra={!hasIncomingDataEdge}
            disabledExtraTitle="Connect an upstream provider to enable AI chat."
            minRows={2}
            maxRows={12}
          />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Local helpers
// ──────────────────────────────────────────────────────────────────────


/**
 * RawSpecDisclosure — editable typed `EchartsVizSpecV1` (ADR-0079).
 * Collapsed by default. Edits are validated via `validateEchartsSpec`;
 * a valid parse on blur commits to node state (autosaved by the editor).
 * Invalid edits stay in the textarea and surface errors inline so the user
 * can fix them without losing their work.
 */
function RawSpecDisclosure({
  spec,
  onCommit,
}: {
  spec: EchartsVizSpecV1;
  onCommit: (next: EchartsVizSpecV1) => void;
}) {
  const toast = useToast();
  const canonical = useMemo(() => JSON.stringify(spec, null, 2), [spec]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState<string>(canonical);
  const [errors, setErrors] = useState<string[] | null>(null);

  // Sync the draft when the upstream spec changes (e.g. AI-applied edit) and
  // the user isn't actively editing — detected by the draft matching the last
  // canonical we observed. Avoids stomping on in-progress hand edits.
  const lastCanonicalRef = useRef(canonical);
  useEffect(() => {
    if (draft === lastCanonicalRef.current) {
      setDraft(canonical);
      setErrors(null);
    }
    lastCanonicalRef.current = canonical;
  }, [canonical, draft]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(draft);
      toast.push({ kind: "success", message: "Copied spec JSON" });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }, [draft, toast]);

  const reset = useCallback(() => {
    setDraft(canonical);
    setErrors(null);
  }, [canonical]);

  const commit = useCallback(() => {
    if (draft === canonical) {
      setErrors(null);
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch (e) {
      setErrors([`JSON parse error: ${e instanceof Error ? e.message : String(e)}`]);
      return;
    }
    const result = validateEchartsSpec(parsed);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors(null);
    onCommit(result.spec);
  }, [draft, canonical, onCommit]);

  const dirty = draft !== canonical;

  return (
    <details style={{ marginTop: 8 }}>
      <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>
        Raw spec JSON ({spec.chartType}){dirty ? " — unsaved" : ""}
      </summary>
      <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
        <button className="btn btnSm" type="button" onClick={() => void copy()}>Copy</button>
        <button className="btn btnSm" type="button" onClick={reset} disabled={!dirty}>Reset</button>
        <button className="btn btnSm btnPrimary" type="button" onClick={commit} disabled={!dirty}>Apply</button>
      </div>
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={commit}
        spellCheck={false}
        className="mono"
        style={{
          marginTop: 6,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          fontSize: 11,
          height: 320,
          padding: 8,
          background: "var(--surface-alt)",
          border: errors ? "1px solid var(--danger-text)" : "1px solid var(--line)",
          borderRadius: 4,
          resize: "vertical",
          whiteSpace: "pre",
        }}
      />
      {errors ? (
        <div className="help" style={{ color: "var(--danger-text)", marginTop: 4 }}>
          {errors.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </div>
      ) : null}
    </details>
  );
}

